<script setup lang="ts">
/**
 * 对话文本编辑器
 *
 * 提供多行文本输入，支持变量/字符串插入、选区着色与自动换行编码。
 */
import { computed, nextTick, ref, watch } from 'vue'
import AppColorPickerPanel from '@/components/AppColorPickerPanel.vue'
import VariableIndexPickerModal from '@/components/eventEditors/VariableIndexPickerModal.vue'
import type { VariableIndexScope } from '@/features/event/variableIndex'
import {
  decodeDialogTextForEdit,
  encodeDialogTextFromEdit,
} from '@/features/dialog-text/wrap'
import { hexToRgbString, normalizeRgbString, rgbStringToHex } from '@/utils/textColor'
import { stripEmoji } from '@/utils/plainTextInput'

defineOptions({
  inheritAttrs: false,
})

/** 右键菜单可执行的变量插入动作。 */
type InsertAction = {
  key: string
  label: string
  scope: VariableIndexScope
  token: 'v' | 'x' | 't'
}

/** 变量/字符串插入菜单项。 */
const INSERT_ACTIONS: InsertAction[] = [
  { key: 'var', label: '插入数值', scope: 'var', token: 'v' },
  { key: 'varEx', label: '插入二周目', scope: 'varEx', token: 'x' },
  { key: 'string', label: '插入字符串', scope: 'string', token: 't' },
]

// props
const props = withDefaults(defineProps<{
  modelValue: string
  rows?: number
  enableColor?: boolean
  enableVariableInsert?: boolean
  enableAutoWrap?: boolean
  autoWrapWidth?: number
  autoWrapFont?: string
  measureCharWidth?: (char: string) => number
  restoreColor?: string
}>(), {
  rows: 6,
  enableColor: false,
  enableVariableInsert: true,
  enableAutoWrap: false,
  autoWrapWidth: 0,
  autoWrapFont: '',
  restoreColor: '',
})

// emits
const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

// state
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const editValue = ref('')
const menuOpen = ref(false)
const menuX = ref(0)
const menuY = ref(0)
const pickerOpen = ref(false)
const pickerScope = ref<VariableIndexScope>('var')
const pickerTitle = ref('')
const pickerToken = ref<'v' | 'x' | 't'>('v')
const savedSelection = ref({ start: 0, end: 0 })
const colorPanelOpen = ref(false)
const selectedColorHex = ref('#ffffff')
const openedColorHex = ref('#ffffff')
const colorAnchorLeft = ref(0)
const colorAnchorTop = ref(0)
const colorAnchorHeight = ref(28)

// computed
const normalizedRestoreColor = computed(() => normalizeRgbString(props.restoreColor))
const canWrapColor = computed(() =>
  props.enableColor && savedSelection.value.end > savedSelection.value.start && Boolean(normalizedRestoreColor.value),
)
const menuStyle = computed(() => ({
  left: `${menuX.value}px`,
  top: `${menuY.value}px`,
}))
const visibleInsertActions = computed(() => props.enableVariableInsert ? INSERT_ACTIONS : [])
const textAreaStyle = computed(() => {
  const width = Math.round(Number(props.autoWrapWidth || 0))
  return props.enableAutoWrap && width > 0
    ? { maxWidth: `${width}px` }
    : undefined
})

// helpers
/** 将编辑区纯文本编码为引擎存储格式。 */
function encodeEditValue(value: string): string {
  return encodeDialogTextFromEdit({
    text: value,
    enableAutoWrap: props.enableAutoWrap,
    autoWrapWidth: Math.round(Number(props.autoWrapWidth || 0)),
    autoWrapFont: props.autoWrapFont,
    measureCharWidth: props.measureCharWidth,
  })
}

/** 更新本地编辑值并向父组件 emit 编码结果。 */
function emitInput(value: string): void {
  const sanitized = stripEmoji(value)
  editValue.value = sanitized
  emit('update:modelValue', encodeEditValue(sanitized))
}

// watch
watch(
  () => props.modelValue,
  (value) => {
    const decoded = decodeDialogTextForEdit(value)
    if (editValue.value === decoded) return
    editValue.value = decoded
  },
  { immediate: true },
)

watch(
  () => [props.autoWrapWidth, props.autoWrapFont, props.enableAutoWrap] as const,
  () => {
    const next = encodeEditValue(editValue.value)
    if (next !== props.modelValue) emit('update:modelValue', next)
  },
)

// handlers
function saveSelection(): void {
  const textarea = textareaRef.value
  if (!textarea) return
  savedSelection.value = {
    start: textarea.selectionStart,
    end: textarea.selectionEnd,
  }
}

function closeMenu(): void {
  menuOpen.value = false
}

function handleKeydown(event: KeyboardEvent): void {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
    event.preventDefault()
    event.stopPropagation()
  }
}

function handleContextMenu(event: MouseEvent): void {
  if (!props.enableVariableInsert && !props.enableColor) return
  event.preventDefault()
  event.stopPropagation()
  saveSelection()
  openMenuAt(event.clientX, event.clientY)
}

function openMenuAt(clientX: number, clientY: number): void {
  const height = props.enableColor ? 128 : 98
  const width = 132
  const margin = 8
  menuX.value = Math.max(margin, Math.min(clientX, window.innerWidth - width - margin))
  menuY.value = Math.max(margin, Math.min(clientY, window.innerHeight - height - margin))
  menuOpen.value = true
}

function handleMoreClick(event: MouseEvent): void {
  event.preventDefault()
  event.stopPropagation()
  saveSelection()
  const target = event.currentTarget as HTMLElement
  const rect = target.getBoundingClientRect()
  openMenuAt(rect.right, rect.bottom)
}

function replaceSelection(text: string, nextCaretOffset = text.length): void {
  const { start, end } = savedSelection.value
  const current = editValue.value
  const next = `${current.slice(0, start)}${text}${current.slice(end)}`
  emitInput(next)
  closeMenu()
  void nextTick(() => {
    const textarea = textareaRef.value
    if (!textarea) return
    const caret = start + nextCaretOffset
    textarea.focus()
    textarea.setSelectionRange(caret, caret)
  })
}

function openVariablePicker(action: InsertAction): void {
  saveSelection()
  pickerScope.value = action.scope
  pickerToken.value = action.token
  pickerTitle.value = action.label
  pickerOpen.value = true
  closeMenu()
}

function handleVariableSelect(index: number): void {
  const order = Math.max(1, Math.round(Number(index) || 0) + 1)
  replaceSelection(`\\${pickerToken.value}[${order}]`)
}

function openColorPicker(): void {
  if (!canWrapColor.value) return
  openedColorHex.value = rgbStringToHex(normalizedRestoreColor.value, '#ffffff')
  selectedColorHex.value = openedColorHex.value
  colorAnchorLeft.value = menuX.value
  colorAnchorTop.value = menuY.value
  const itemCount = visibleInsertActions.value.length + 1
  colorAnchorHeight.value = 8 + itemCount * 30
  closeMenu()
  colorPanelOpen.value = true
}

function handleColorConfirm(hex: string): void {
  colorPanelOpen.value = false
  applyColorWrap(hex)
}

function handleColorCancel(): void {
  colorPanelOpen.value = false
  selectedColorHex.value = openedColorHex.value
}

function applyColorWrap(value: string): void {
  if (!canWrapColor.value) return
  const { start, end } = savedSelection.value
  const current = editValue.value
  const selectedText = current.slice(start, end)
  const color = hexToRgbString(value)
  const restoreColor = normalizedRestoreColor.value
  const wrapped = `\\c[${color}]${selectedText}\\c[${restoreColor}]`
  replaceSelection(wrapped, wrapped.length)
}
</script>

<template>
  <div class="dialog-text-editor">
    <textarea
      ref="textareaRef"
      v-bind="$attrs"
      class="dialog-text-editor__textarea"
      :rows="rows"
      :style="textAreaStyle"
      :value="editValue"
      @input.stop="emitInput(($event.target as HTMLTextAreaElement).value)"
      @keydown="handleKeydown"
      @contextmenu="handleContextMenu"
      @click="closeMenu"
    />
    <button
      v-if="enableVariableInsert || enableColor"
      type="button"
      class="dialog-text-editor__more"
      title="更多文本操作"
      @click="handleMoreClick"
    >
      更多
    </button>

    <Teleport to="body">
      <div
        v-if="menuOpen"
        class="dialog-text-editor__menu"
        :style="menuStyle"
        @contextmenu.prevent.stop
        @mousedown.stop
      >
        <button
          v-for="action in visibleInsertActions"
          :key="action.key"
          type="button"
          class="dialog-text-editor__menu-item"
          @click="openVariablePicker(action)"
        >
          {{ action.label }}
        </button>
        <button
          v-if="enableColor"
          type="button"
          class="dialog-text-editor__menu-item"
          :disabled="!canWrapColor"
          @click="openColorPicker"
        >
          设置颜色
        </button>
      </div>
    </Teleport>

    <AppColorPickerPanel
      v-model:show="colorPanelOpen"
      v-model="selectedColorHex"
      :anchor-left="colorAnchorLeft"
      :anchor-top="colorAnchorTop"
      :anchor-height="colorAnchorHeight"
      @confirm="handleColorConfirm"
      @cancel="handleColorCancel"
    />

    <VariableIndexPickerModal
      v-if="pickerOpen"
      v-model:show="pickerOpen"
      :selected-index="0"
      :scope="pickerScope"
      :title="pickerTitle"
      @select="handleVariableSelect"
    />
  </div>
</template>

<style scoped>
.dialog-text-editor {
  position: relative;
  width: 100%;
  min-width: 0;
}

.dialog-text-editor__textarea {
  width: 100%;
  min-width: 0;
  min-height: 120px;
  padding: 4px 8px 28px;
  border: 1px solid var(--theme-bg-secondary);
  background: var(--theme-bg-surface);
  color: var(--theme-text-heading);
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.dialog-text-editor__textarea:focus {
  outline: none;
  border-color: var(--theme-accent);
}

.dialog-text-editor__more {
  position: absolute;
  right: 2px;
  bottom: 6px;
  height: 22px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: var(--theme-text-secondary);
  font-size: 12px;
  cursor: pointer;
}

.dialog-text-editor__more:hover {
  color: var(--theme-text-heading);
}

.dialog-text-editor__menu {
  position: fixed;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  min-width: 128px;
  padding: 4px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.dialog-text-editor__menu-item {
  width: 100%;
  padding: 6px 10px;
  border: 0;
  background: var(--theme-bg-surface);
  color: var(--theme-text-heading);
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.dialog-text-editor__menu-item:hover {
  background: var(--theme-bg-secondary);
}

.dialog-text-editor__menu-item:disabled {
  color: var(--theme-text-muted);
  cursor: not-allowed;
}
</style>
